import { CheckmarkCircle02Icon, Copy01Icon, Download01Icon, PackageReceiveIcon, Shield02Icon } from '@hugeicons/core-free-icons';
import { useState } from 'react';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/Button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { downloadLicenseFile, downloadReleaseFile, type ConsoleState } from '../../lib/api';

const onPremInstallationSteps = [
  'Verify the downloaded package checksum.',
  'Back up the current on-prem deployment.',
  'Review the release notes before replacing files.',
  'Restart services and confirm deployment health.',
];

const workosInstallationSteps = [
  'Verify the downloaded package checksum.',
  'Back up the current customer-managed deployment.',
  'Confirm the organization and callback configuration.',
  'Deploy the build, restart services, and verify sign-in health.',
];

export function UpdatesPage({ state }: { state: ConsoleState }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const latest = state.releases[0];
  const isWorkosBuild = (latest?.deploymentModel ?? state.license?.deploymentModel) === 'saas_hybrid';
  const installationSteps = isWorkosBuild ? workosInstallationSteps : onPremInstallationSteps;
  const domains = state.license?.licensedDomains ?? [];

  async function downloadBuild() {
    if (!latest) return;
    setBusy('build');
    setMessage(null);
    try {
      await downloadReleaseFile(latest);
      setMessage('Build download started. Verify its SHA-256 checksum before installation.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Download failed.');
    } finally {
      setBusy(null);
    }
  }

  async function copyChecksum() {
    if (!latest) return;
    try {
      await navigator.clipboard.writeText(latest.sha256);
      setMessage('SHA-256 checksum copied.');
    } catch {
      setMessage('The checksum could not be copied.');
    }
  }

  async function downloadLicense(domain: string) {
    setBusy(domain);
    setMessage(null);
    try {
      await downloadLicenseFile(domain);
      setMessage(`License download started for ${domain}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Download failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
      <Card className="h-full gap-0 py-0" size="sm">
        <CardHeader className="p-5 pb-4">
          <CardTitle>{latest ? `myPDF ${latest.version}` : 'No release available'}</CardTitle>
          <CardDescription>{latest?.releaseNotes ?? 'Published packages matching your deployment model will appear here.'}</CardDescription>
          <CardAction className="flex gap-2">
            <Badge className="capitalize" variant="secondary">{state.license?.releaseChannel ?? 'stable'}</Badge>
            <Badge variant="outline">{isWorkosBuild ? 'SaaS / Hybrid' : 'On-prem'}</Badge>
          </CardAction>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col gap-4 px-5 pb-5">
          {latest ? (
            <>
              <div className="rounded-lg bg-muted p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Latest release</p>
                    <p className="mt-1 text-lg font-semibold">Version {latest.version}</p>
                  </div>
                  <HugeIcon className="text-muted-foreground" icon={PackageReceiveIcon} size={20} />
                </div>
                <p className="mt-3 text-xs leading-5 text-muted-foreground">{isWorkosBuild ? 'Ready for a managed identity deployment.' : 'Ready for a customer-controlled on-prem installation.'}</p>
              </div>

              <dl className="grid divide-y rounded-lg border bg-background sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <ReleaseFact label="Package" value={latest.fileName} />
                <ReleaseFact label="Size" value={formatBytes(latest.sizeBytes)} />
                <ReleaseFact label="Published" value={latest.publishedAt ? formatDate(latest.publishedAt) : 'Draft'} />
              </dl>

              <div className="flex min-h-12 items-center gap-3 rounded-lg border bg-background px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">SHA-256 checksum</p>
                  <p className="truncate font-mono text-[11px] text-foreground">{latest.sha256}</p>
                </div>
                <Button aria-label="Copy checksum" onClick={() => void copyChecksum()} size="icon-sm" type="button" variant="outline">
                  <HugeIcon icon={Copy01Icon} size={14} />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center rounded-lg bg-muted px-4 py-8 text-center text-sm text-muted-foreground">No build has been published for this release channel.</div>
          )}

          <Button className="mt-auto w-full" disabled={!latest || busy === 'build'} onClick={() => void downloadBuild()} type="button">
            <HugeIcon icon={Download01Icon} size={16} />
            {busy === 'build' ? 'Preparing…' : 'Download build'}
          </Button>
        </CardContent>
      </Card>

      <Card className="h-full gap-0 py-0" size="sm">
        <CardHeader className="p-5 pb-4">
          <CardTitle>Install safely</CardTitle>
          <CardDescription>Complete these checks for every customer-managed update.</CardDescription>
          <CardAction><HugeIcon className="text-muted-foreground" icon={Shield02Icon} size={19} /></CardAction>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          <ol className="divide-y rounded-lg border bg-background px-3">
            {installationSteps.map((step, index) => (
              <li className="flex min-h-14 items-center gap-3 py-2" key={step}>
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">{index + 1}</span>
                <span className="text-sm leading-5">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-muted px-3 py-2.5 text-xs text-muted-foreground">
            <HugeIcon icon={CheckmarkCircle02Icon} size={16} />
            Keep the current build available until verification succeeds.
          </div>
        </CardContent>
      </Card>

      {!isWorkosBuild ? <Card className="gap-0 py-0 lg:col-span-2" size="sm">
        <CardHeader className="p-5 pb-4">
          <CardTitle>Deployment license files</CardTitle>
          <CardDescription>Download the matching active license with the build package.</CardDescription>
          <CardAction><span className="text-xs text-muted-foreground">{domains.length} {domains.length === 1 ? 'domain' : 'domains'}</span></CardAction>
        </CardHeader>
        <CardContent className={domains.length > 1 ? 'grid gap-3 px-5 pb-5 sm:grid-cols-2' : 'grid gap-3 px-5 pb-5'}>
          {domains.map((domain) => (
            <div className="flex min-h-14 items-center gap-3 rounded-lg border bg-background px-3 py-2.5" key={domain}>
              <HugeIcon className="text-muted-foreground" icon={PackageReceiveIcon} size={18} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{domain}</p>
                <p className="text-xs text-muted-foreground">Valid until {state.license?.expiryDate ?? 'not set'}</p>
              </div>
              <Button
                aria-label={`Download license for ${domain}`}
                disabled={busy === domain || !state.license?.downloadAvailable}
                onClick={() => void downloadLicense(domain)}
                size="sm"
                type="button"
                variant="outline"
              >
                <HugeIcon icon={Download01Icon} size={15} />
                {busy === domain ? 'Preparing…' : 'Download'}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card> : null}

      {message ? <p aria-live="polite" className="text-xs text-muted-foreground lg:col-span-2">{message}</p> : null}
    </section>
  );
}

function ReleaseFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 px-3 py-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 truncate font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function formatBytes(bytes: number) {
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
}
